import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type BaseMessage } from "@sendbird/chat/message";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTu } from "@src/appV2/i18n/useTu";

interface UseMarkChannelAsUnreadParams {
  workplaceId: string;
  workerId: string;
}

interface MarkAsUnreadArguments {
  channel: GroupChannel;
  message: BaseMessage;
}

/**
 * Wraps Sendbird's `GroupChannel.markAsUnread(message)` with query invalidation
 * so the unread-count badge updates immediately. The native API marks everything
 * from `message` onward as unread and syncs the state server-side.
 *
 * Exposes the standard `useMutation` surface — callers use `isLoading` to gate
 * their UI while the round-trip is in flight.
 */
export function useMarkChannelAsUnread(params: UseMarkChannelAsUnreadParams) {
  const { tu } = useTu("chat");
  const { workplaceId, workerId } = params;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channel, message }: MarkAsUnreadArguments) => {
      await channel.markAsUnread(message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["chat", "unreadCount", workplaceId, workerId],
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.MARK_CHAT_AS_UNREAD_FAILURE,
      userErrorMessage: tu("Failed to mark chat as unread"),
    },
  });
}
